import React, { useState, useEffect } from 'react';
import Network from '../../../models/Network.js';
import AccordionListElement from '../list_elements/AccordionListElement.js';
import './Module.css';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')
const ErrorCodes = require('../../../error-codes/error-codes.js')

const ReportTimelineTextModule = ({user, reportId, isSmallScreen}) => {
    const [reportXml, setReportXml] = useState(null);
    const [reportJson, setReportJson] = useState(null);
    const [reportObject, setReportObject] = useState(null);
    const [reportSchemaMessages, setReportSchemaMessages] = useState(null);
    const [reportTimelineText, setReportTimelineText] = useState(null);
    const [reportTimelineEvents, setReportTimelineEvents] = useState(null);

    // Function to format timeline events with color information
    const formatTimelineEvents = (timeline) => {
        if (!timeline || !Array.isArray(timeline) || timeline.length === 0) {
            return [];
        }

        // Sort events by timestamp to ensure chronological order
        const sortedEvents = [...timeline].sort((a, b) => {
            const timeA = new Date(a.timeStamp).getTime();
            const timeB = new Date(b.timeStamp).getTime();
            return timeA - timeB;
        });

        // Find unit back in service event to truncate timeline (same logic as ReportTimeline.js)
        const backInServiceEvent = sortedEvents.find(
            e => e.eventType === 'TIME' && e.data?.specification === 'UNIT_IN_SERVICE'
        );

        // Filter events to only include those before or at the unit back in service time
        const filteredEvents = backInServiceEvent
            ? sortedEvents.filter(e => new Date(e.timeStamp).getTime() <= new Date(backInServiceEvent.timeStamp).getTime())
            : sortedEvents;

        // Format each event with event type for coloring
        const formattedEvents = filteredEvents.map(event => {
            const timestamp = new Date(event.timeStamp);
            const timeString = timestamp.toLocaleString();
            
            // Generate header line based on event type
            let header = '';
            if (event.eventType === 'TIME') {
                const spec = event.data?.specification || 'UNKNOWN_TIME';
                // Convert specification to readable format
                const specMap = {
                    'PSAP_CALL': 'PSAP Call',
                    'DISPATCH_NOTIFIED': 'Dispatch Notified',
                    'UNIT_NOTIFIED': 'Unit Notified',
                    'DISPATCH_ACK': 'Dispatch Acknowledged',
                    'UNIT_ENROUTE': 'Unit En Route',
                    'UNIT_ON_SCENE': 'Unit On Scene',
                    'PATIENT_CONTACT': 'Unit Arrived at Patient',
                    'TRANSFER_OF_EMS_CARE': 'Transfer of EMS Care',
                    'UNIT_LEFT_SCENE': 'Unit Left Scene',
                    'ARRIVAL_AT_DEST_LANDING_AREA': 'Arrival at Destination Landing Area',
                    'PATIENT_AT_DESTINATION': 'Patient at Destination',
                    'DESTINATION_TRANSFER': 'Destination Transfer of Care',
                    'UNIT_IN_SERVICE': 'Unit Back in Service',
                    'UNIT_CANCELLED': 'Unit Cancelled',
                    'UNIT_HOME': 'Unit Home',
                    'EMS_CALL_COMPLETED': 'EMS Call Completed',
                    'ARRIVAL_AT_STAGING': 'Arrival at Staging'
                };
                header = `${timeString} - ${specMap[spec] || spec}`;
            } else if (event.eventType === 'PROCEDURE_PERFORMED') {
                const procedure = event.data?.procedure || 'Unknown Procedure';
                header = `${timeString} - Procedure: ${procedure}`;
            } else if (event.eventType === 'MEDICATION_GIVEN') {
                const medication = event.data?.medication || 'Unknown Medication';
                header = `${timeString} - Medication: ${medication}`;
            } else if (event.eventType === 'VITALS_TAKEN') {
                header = `${timeString} - Vitals Taken`;
            } else if (event.eventType === 'CARDIAC_ARREST') {
                header = `${timeString} - Cardiac Arrest`;
            } else if (event.eventType === 'CPR_INITIATED') {
                const provider = event.data?.provider ? ` by ${event.data.provider}` : '';
                header = `${timeString} - CPR Initiated${provider}`;
            } else if (event.eventType === 'RESUSCITATION_ENDED') {
                header = `${timeString} - Resuscitation Ended`;
            } else if (event.eventType === 'AIRWAY_CONFIRMATION') {
                const device = event.data?.device || 'Unknown Device';
                header = `${timeString} - Airway Confirmation: ${device}`;
            } else if (event.eventType === 'DEVICE_EVENT') {
                const eventType = event.data?.eventType || 'Unknown Event';
                header = `${timeString} - Device Event: ${eventType}`;
            } else {
                header = `${timeString} - ${event.eventType}`;
            }

            // Special handling for VITALS_TAKEN events
            if (event.eventType === 'VITALS_TAKEN' && event.data?.vitals) {
                const vitals = event.data.vitals;
                // Map of vital keys to display names
                const vitalNames = {
                    'sbp': 'SBP',
                    'dbp': 'DBP',
                    'hr': 'HR',
                    'rr': 'RR',
                    'spo2': 'SpO2',
                    'etco2': 'ETCO2',
                    'temp': 'Temp',
                    'gcs': 'GCS',
                    'glucose': 'Glucose',
                    'obtainedPriorToCrewArrival': 'Obtained Prior to Crew Arrival'
                };
                
                // Format vitals readings, filtering out null values, all on one line
                const vitalReadings = Object.entries(vitals)
                    .filter(([key, value]) => value !== null && value !== undefined && value !== '')
                    .map(([key, value]) => {
                        const displayName = vitalNames[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
                        return `${displayName}: ${value}`;
                    });
                
                const bulletHeader = `• ${header}`;
                const fullText = vitalReadings.length > 0 
                    ? bulletHeader + '  ' + vitalReadings.join(', ')
                    : bulletHeader;
                return { text: fullText, eventType: event.eventType };
            }

            // Format data line (on one line, shortened) for other event types
            let dataLine = '';
            if (event.data && Object.keys(event.data).length > 0) {
                // Format data object nicely, all on one line
                const dataEntries = Object.entries(event.data)
                    .filter(([key]) => {
                        // Filter out specification for TIME events as it's already in header
                        if (event.eventType === 'TIME' && key === 'specification') {
                            return false;
                        }
                        // Filter out procedure/medication for those events as they're in header
                        if ((event.eventType === 'PROCEDURE_PERFORMED' && key === 'procedure') ||
                            (event.eventType === 'MEDICATION_GIVEN' && key === 'medication') ||
                            (event.eventType === 'AIRWAY_CONFIRMATION' && key === 'device') ||
                            (event.eventType === 'DEVICE_EVENT' && key === 'eventType')) {
                            return false;
                        }
                        // Filter out vitals for VITALS_TAKEN (handled separately above)
                        if (event.eventType === 'VITALS_TAKEN' && key === 'vitals') {
                            return false;
                        }
                        return true;
                    })
                    .map(([key, value]) => {
                        // Format key to be more readable, shortened
                        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
                        // Shorten value if it's too long
                        const valueStr = String(value);
                        const maxValueLength = 50;
                        const shortenedValue = valueStr.length > maxValueLength 
                            ? valueStr.substring(0, maxValueLength) + '...' 
                            : valueStr;
                        return `${formattedKey}: ${shortenedValue}`;
                    });
                
                if (dataEntries.length > 0) {
                    dataLine = '  ' + dataEntries.join(', ');
                }
            }

            // Add bullet to header and combine with data on one line
            const bulletHeader = `• ${header}`;
            const fullText = bulletHeader + (dataLine ? ' ' + dataLine : '');
            return { text: fullText, eventType: event.eventType };
        });

        return formattedEvents;
    };

    // Function to format timeline events as plain text (for backward compatibility)
    const formatTimelineAsText = (timeline) => {
        const events = formatTimelineEvents(timeline);
        if (events.length === 0) {
            return 'No timeline events available.';
        }
        return events.map(e => e.text).join('\n');
    };

    // function that gets one reports from the server
    const getOneReport = async (reportId) => {
        const url = URLS.GET_ONE_REPORT;
        const dict = {
            uid: user.uid,
            report_id: reportId,
            CLIENT_ACCESS_KEY: key
        };
        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                // do something
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error")
            } else {
                console.log("Unknown Error")
            }
        });
        return responseData;
    };

    useEffect(() => {
        getOneReport(reportId)
            .then((data) => {
                try {
                    const parsedJSON = JSON.parse(data[0].report_json);
                    setReportJson(parsedJSON);
                    console.log('ReportTimelineTextModule - report JSON:', parsedJSON);

                    // setReportSchemaMessages(JSON.parse(data[0].report_schema_messages));
                    // setReportXml(data[0].report_xml);
                    const parsedReportObject = JSON.parse(data[0].report_object);
                    setReportObject(parsedReportObject);
                    if (parsedReportObject.reportTimeline) {
                        const formattedEvents = formatTimelineEvents(parsedReportObject.reportTimeline);
                        setReportTimelineEvents(formattedEvents);
                        const formattedText = formatTimelineAsText(parsedReportObject.reportTimeline);
                        setReportTimelineText(formattedText);
                        // console.log('ReportTimelineTextModule - formatted timeline events:', formattedEvents);
                    }
                } catch (error) {
                    console.log('Error parsing JSON:', error);
                    setReportJson(null);
                    setReportSchemaMessages(null);
                    setReportXml(null);
                    setReportObject(null);
                }
            })
            .catch((error) => {
                console.log('Error:', error);
            });
    }, []);

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            {/* {reportJson ? (
                <AccordionListElement reportJSON={reportJson} />
            ) : (
                <div>Loading Report Data...</div>
            )} */}
            <div>
                {/* <h2 className='module-title'>Report Timeline Text</h2> */}
                {reportTimelineEvents ? (
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', textAlign: 'left' }}>
                        {reportTimelineEvents.map((event, index) => {
                            // Determine color based on event type (matching ReportTimeline.js)
                            let color = '#000'; // default black
                            if (event.eventType === 'MEDICATION_GIVEN') {
                                color = '#15c240'; // green
                            } else if (event.eventType === 'VITALS_TAKEN') {
                                color = '#ff6b6b'; // red
                            } else if (event.eventType === 'PROCEDURE_PERFORMED' || 
                                      event.eventType === 'AIRWAY_CONFIRMATION' ||
                                      event.eventType === 'CARDIAC_ARREST' ||
                                      event.eventType === 'CPR_INITIATED' ||
                                      event.eventType === 'RESUSCITATION_ENDED') {
                                color = '#45b7d1'; // blue
                            }
                            
                            // Parse text to underline "Procedure:" and "Medication:" parts
                            const renderTextWithUnderline = (text, eventType) => {
                                if (eventType === 'PROCEDURE_PERFORMED' || eventType === 'MEDICATION_GIVEN') {
                                    // Find the label (Procedure: or Medication:) and its value
                                    // Match from "Procedure:" or "Medication:" up to two spaces (start of data) or end of string
                                    const labelIndex = text.indexOf(eventType === 'PROCEDURE_PERFORMED' ? 'Procedure:' : 'Medication:');
                                    if (labelIndex !== -1) {
                                        // Find where the label/value ends (two spaces or end of string)
                                        const afterLabel = text.substring(labelIndex);
                                        const endMatch = afterLabel.match(/\s{2}/);
                                        const endIndex = endMatch ? labelIndex + endMatch.index : text.length;
                                        
                                        const beforeLabel = text.substring(0, labelIndex);
                                        const labelAndValue = text.substring(labelIndex, endIndex);
                                        const afterValue = text.substring(endIndex);
                                        
                                        return (
                                            <>
                                                {beforeLabel}
                                                <span style={{ textDecoration: 'underline' }}>{labelAndValue}</span>
                                                {afterValue}
                                            </>
                                        );
                                    }
                                }
                                return text;
                            };
                            
                            return (
                                <div key={index} style={{ color: color }}>
                                    {renderTextWithUnderline(event.text, event.eventType)}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div>Loading Report Timeline Text...</div>
                )}
            </div>
            {/* <div>
                <h2 className='module-title'>Report XML</h2>
                <pre>{reportXml}</pre>
            </div> */}
            {/* <div>
                <h2 className='module-title'>Report Timeline</h2>
                {reportObject ? (
                    <>
                        <ReportTimeline reportData={reportObject} />
                    </>
                ) : (
                    <div>Loading Timeline...</div>
                )}
            </div>
            <div>
                <h2 className='module-title'>Report Object</h2>
            </div>
            {reportObject ? (
                <AccordionListElement reportJSON={reportObject} />
            ) : (
                <div>Loading Report Object...</div>
            )} */}
            {/* <div>
                <h2 className='module-title'>Report Schema Messages</h2>
            </div>
            {reportSchemaMessages ? (
                <AccordionListElement reportJSON={reportSchemaMessages} />
            ) : (
                <div>Loading Report Schema Messages...</div>
            )} */}
        </div>
    );
}

export default ReportTimelineTextModule; 